/**
 * PulseFit Dashboard View Controller
 * 
 * Manages dashboard KPI progress rings, weekly trend sparklines,
 * HYROX station progress tiles, achievement badges, and quick adjustments.
 */

import {
  CIRCLE_CIRCUMFERENCE,
  STATION_META,
  HYROX_STATIONS,
  getStationBadgeHtml,
  calculateWorkoutZoneSeconds
} from '../telemetry/index.js';
import {
  getWorkoutSufferScore,
  getWorkoutTimestamp
} from '../performance/index.js';
import {
  getWorkoutTotalDistance
} from '../integrations/index.js';
import {
  store,
  subscribeKey,
  saveStateToStorage
} from '../../store.js';
import { isDateInCurrentWeek, escapeHtml } from './workouts.js';

/**
 * Returns station total distances/reps for the current week.
 */
export function getWeeklyStationTotals(s) {
  const totals = {
    'SkiErg': 0,
    'Sled Push': 0,
    'Sled Pull': 0,
    'Burpee Broad Jumps': 0,
    'Rowing': 0,
    'Farmers Carry': 0,
    'Sandbag Lunges': 0,
    'Wall Balls': 0
  };
  if (s && s.workouts) {
    s.workouts.forEach(w => {
      const isHyrox = w.type === 'Hyrox' || w.type === 'Hyrox Training' || w.type === 'Hyrox Workout' || (w.name && w.name.toLowerCase().includes('hyrox')) || !w.type;
      if (!isHyrox || !Array.isArray(w.stages)) return;
      const ts = getWorkoutTimestamp(w);
      if (!ts) return;
      const localDateStr = new Date(ts).toLocaleDateString('sv');
      if (!isDateInCurrentWeek(localDateStr)) return;

      w.stages.forEach(st => {
        if (!st.stationName || st.type === 'Run' || st.type === 'Rest' || st.type === 'RoxZone' || st.type === 'Cardio') return;
        const key = st.stationName;
        if (totals[key] !== undefined) {
          const metaObj = STATION_META.find(m => m.key === key);
          const val = (metaObj && !metaObj.isDistance) ? (st.reps || st.distance || 0) : (st.distance || st.reps || 0);
          totals[key] += val;
        }
      });
    });
  }
  return totals;
}

export const BADGES = [
  {
    id: 'badge-suffer',
    category: 'Weekly Core Metrics',
    title: 'Peak Suffering',
    desc: (s) => `Reach a weekly Suffer Score of ${(s.goals && s.goals.suffer) || 550}.`,
    icon: '🥵',
    check: (s) => ((s.weeklyStats && s.weeklyStats.suffer) || 0) >= ((s.goals && s.goals.suffer) || 550)
  },
  {
    id: 'badge-distance',
    category: 'Weekly Core Metrics',
    title: 'Distance Runner',
    desc: (s) => `Run ${(s.goals && s.goals.distance) || 25.0} km in a single week.`,
    icon: '🏃',
    check: (s) => ((s.weeklyStats && s.weeklyStats.distance) || 0) >= ((s.goals && s.goals.distance) || 25.0)
  },
  {
    id: 'badge-calories',
    category: 'Weekly Core Metrics',
    title: 'Calorie Crusher',
    desc: (s) => `Burn ${(s.goals && s.goals.calories) || 3500} kcal in a single week.`,
    icon: '🔥',
    check: (s) => ((s.weeklyStats && s.weeklyStats.calories) || 0) >= ((s.goals && s.goals.calories) || 3500)
  },
  {
    id: 'badge-minutes',
    category: 'Weekly Core Metrics',
    title: 'Base Builder',
    desc: (s) => `Accumulate ${(s.weeklyZoneGoals && s.weeklyZoneGoals.z2) || 120} minutes of Z2 weekly exercise.`,
    icon: '🟢',
    check: (s) => ((s.weeklyZones && s.weeklyZones.z2) || 0) >= ((s.weeklyZoneGoals && s.weeklyZoneGoals.z2) || 120)
  },
  {
    id: 'badge-anaerobic',
    category: 'Weekly Core Metrics',
    title: 'Anaerobic Beast',
    desc: (s) => `Hit ${(s.weeklyZoneGoals && s.weeklyZoneGoals.z5) || 30} minutes of Z5 weekly exercise.`,
    icon: '🔴',
    check: (s) => ((s.weeklyZones && s.weeklyZones.z5) || 0) >= ((s.weeklyZoneGoals && s.weeklyZoneGoals.z5) || 30)
  },
  {
    id: 'badge-engine',
    category: 'Hyrox Station Badges',
    title: 'Engine Builder',
    desc: (s) => `Hit goals for SkiErg (${(s.goals?.stations || {})['SkiErg'] || 500}m) and Rowing (${(s.goals?.stations || {})['Rowing'] || 500}m).`,
    icon: '🚣',
    check: (s) => {
      const st = getWeeklyStationTotals(s);
      const g = s.goals && s.goals.stations || {};
      return st['SkiErg'] >= (g['SkiErg'] || 500) && st['Rowing'] >= (g['Rowing'] || 500);
    }
  },
  {
    id: 'badge-power',
    category: 'Hyrox Station Badges',
    title: 'Powerhouse',
    desc: (s) => `Hit goals for Sled Push (${(s.goals?.stations || {})['Sled Push'] || 4} sets) and Sled Pull (${(s.goals?.stations || {})['Sled Pull'] || 4} sets).`,
    icon: '🏋️',
    check: (s) => {
      const st = getWeeklyStationTotals(s);
      const g = s.goals && s.goals.stations || {};
      return st['Sled Push'] >= (g['Sled Push'] || 4) && st['Sled Pull'] >= (g['Sled Pull'] || 4);
    }
  },
  {
    id: 'badge-launch',
    category: 'Hyrox Station Badges',
    title: 'Launch Pad',
    desc: (s) => `Hit goal for Burpee Broad Jumps (${(s.goals?.stations || {})['Burpee Broad Jumps'] || 100} reps).`,
    icon: '🤸',
    check: (s) => {
      const st = getWeeklyStationTotals(s);
      const g = s.goals && s.goals.stations || {};
      return st['Burpee Broad Jumps'] >= (g['Burpee Broad Jumps'] || 100);
    }
  },
  {
    id: 'badge-farmers',
    category: 'Hyrox Station Badges',
    title: "Farmer's Walk",
    desc: (s) => `Hit goal for Farmer's Carry (${(s.goals?.stations || {})['Farmers Carry'] || 120}m).`,
    icon: '🎒',
    check: (s) => {
      const st = getWeeklyStationTotals(s);
      const g = s.goals && s.goals.stations || {};
      return st['Farmers Carry'] >= (g['Farmers Carry'] || 120);
    }
  },
  {
    id: 'badge-legs',
    category: 'Hyrox Station Badges',
    title: 'Leg Destroyer',
    desc: (s) => `Hit goals for Sandbag Lunges (${(s.goals?.stations || {})['Sandbag Lunges'] || 100}m) and Wall Balls (${(s.goals?.stations || {})['Wall Balls'] || 100} reps).`,
    icon: '🧳',
    check: (s) => {
      const st = getWeeklyStationTotals(s);
      const g = s.goals && s.goals.stations || {};
      return st['Sandbag Lunges'] >= (g['Sandbag Lunges'] || 100) && st['Wall Balls'] >= (g['Wall Balls'] || 100);
    }
  },
  {
    id: 'badge-hybrid',
    category: 'Training Milestones',
    title: 'Hyrox Hybrid',
    desc: () => 'Log a Hyrox session containing at least 4 different stations in a single workout this week.',
    icon: '🏁',
    check: (s) => {
      if (!s.workouts) return false;
      return s.workouts.some(w => {
        const isHyrox = w.type === 'Hyrox' || w.type === 'Hyrox Training' || w.type === 'Hyrox Workout' || (w.name && w.name.toLowerCase().includes('hyrox')) || !w.type;
        if (!isHyrox || !Array.isArray(w.stages)) return false;
        const ts = getWorkoutTimestamp(w);
        if (!ts) return false;
        const localDateStr = new Date(ts).toLocaleDateString('sv');
        if (!isDateInCurrentWeek(localDateStr)) return false;
        const uniqueStations = new Set(w.stages.filter(st => st.stationName && st.type !== 'Run' && st.type !== 'Rest' && st.type !== 'RoxZone' && st.type !== 'Cardio').map(st => st.stationName));
        return uniqueStations.size >= 4;
      });
    }
  },
  {
    id: 'badge-consistency',
    category: 'Training Milestones',
    title: 'Consistency Champion',
    desc: () => 'Log a workout on 4 or more different days of the current week.',
    icon: '🏆',
    check: (s) => {
      if (!s.workouts) return false;
      const activeDays = new Set();
      s.workouts.forEach(w => {
        const ts = getWorkoutTimestamp(w);
        if (!ts) return;
        const localDateStr = new Date(ts).toLocaleDateString('sv');
        if (isDateInCurrentWeek(localDateStr)) {
          activeDays.add(localDateStr);
        }
      });
      return activeDays.size >= 4;
    }
  },
  {
    id: 'badge-double',
    category: 'Training Milestones',
    title: 'Double Session',
    desc: () => 'Log 2 or more workouts in a single day this week.',
    icon: '👥',
    check: (s) => {
      if (!s.workouts) return false;
      const dayCounts = {};
      s.workouts.forEach(w => {
        const ts = getWorkoutTimestamp(w);
        if (!ts) return;
        const localDateStr = new Date(ts).toLocaleDateString('sv');
        if (isDateInCurrentWeek(localDateStr)) {
          dayCounts[localDateStr] = (dayCounts[localDateStr] || 0) + 1;
        }
      });
      return Object.values(dayCounts).some(c => c >= 2);
    }
  },
  {
    id: 'badge-legend',
    category: 'Training Milestones',
    title: 'Fit Legend',
    desc: () => 'Reach weekly goals for Suffer Score, Running, Calories, and at least 3 Hyrox stations.',
    icon: '👑',
    check: (s) => {
      const hasCore = 
        ((s.weeklyStats && s.weeklyStats.suffer) || 0) >= ((s.goals && s.goals.suffer) || 550) &&
        ((s.weeklyStats && s.weeklyStats.distance) || 0) >= ((s.goals && s.goals.distance) || 25.0) &&
        ((s.weeklyStats && s.weeklyStats.calories) || 0) >= ((s.goals && s.goals.calories) || 3500);
      if (!hasCore) return false;
      const st = getWeeklyStationTotals(s);
      const g = s.goals && s.goals.stations || {};
      let reached = 0;
      Object.keys(st).forEach(k => {
        const target = g[k] || 0;
        if (target > 0 && st[k] >= target) reached++;
      });
      return reached >= 3;
    }
  }
];

export function updateRing(elementId, value, goal) {
  if (typeof document === 'undefined') return;
  const ring = document.getElementById(elementId);
  if (!ring) return;
  
  ring.style.strokeDasharray = CIRCLE_CIRCUMFERENCE;
  const percentage = Math.min((value / (goal || 1)) * 100, 100);
  const offset = CIRCLE_CIRCUMFERENCE - (percentage / 100) * CIRCLE_CIRCUMFERENCE;
  ring.style.strokeDashoffset = offset;
}

export function getTitleWithEmoji(w) {
  let title = w.name || w.type || 'Workout';
  const emojiRegex = /[\u{1F300}-\u{1F6FF}\u{1F900}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/u;
  if (emojiRegex.test(title)) {
    return title;
  }
  
  if (title.toLowerCase().includes('hyrox training')) {
    return `${title} 🏋️‍♂️`;
  }
  if (title.toLowerCase().includes('hyrox race')) {
    return `${title} 🏁`;
  }
  
  let emoji = '';
  if (w.type === 'Run') emoji = '🏃';
  else if (w.type === 'Cycle') emoji = '🚴';
  else if (w.type === 'Strength') emoji = '🏋️';
  else if (w.type === 'Walk') emoji = '🚶';
  else if (w.type === 'Yoga') emoji = '🧘';
  else if (w.type === 'Swim') emoji = '🏊';
  else if (w.type === 'Dance') emoji = '💃';
  else if (w.type === 'Hyrox' || w.type === 'Hyrox Training') emoji = '⚡';
  
  if (emoji) {
    return `${title} ${emoji}`;
  }
  return title;
}

export function getWeeklyTrendData(metricType) {
  const dailyValues = [0, 0, 0, 0, 0, 0, 0]; // Mon, Tue, Wed, Thu, Fri, Sat, Sun
  if (!store.workouts) return dailyValues;
  
  store.workouts.forEach(w => {
    const ts = getWorkoutTimestamp(w);
    if (!ts) return;
    
    const localDateStr = new Date(ts).toLocaleDateString('sv');
    if (isDateInCurrentWeek(localDateStr)) {
      const d = new Date(ts);
      const day = d.getDay(); // 0 = Sun, 1 = Mon, ..., 6 = Sat
      const idx = day === 0 ? 6 : day - 1;
      
      if (metricType === 'suffer' && !w.excludeFromStats) {
        const wSuffer = getWorkoutSufferScore(w);
        if (wSuffer > 0) {
          dailyValues[idx] += wSuffer;
        }
      } else if (metricType === 'distance') {
        let dist = 0;
        if (w.type === 'Run') {
          dist = w.distance || 0;
        } else if (w.stages && Array.isArray(w.stages)) {
          w.stages.forEach(st => {
            if (st.type === 'Run' || st.stage_type === 'Run') {
              const stDist = Number(st.distance !== null && st.distance !== undefined ? st.distance : st.distance_meters);
              if (Number.isFinite(stDist) && stDist > 0) {
                dist += (stDist / 1000);
              }
            }
          });
        }
        if (dist > 0) {
          dailyValues[idx] += dist;
        }
      } else if (metricType === 'zones') {
        const zSec = calculateWorkoutZoneSeconds(w);
        const totalZoneMin = Math.round(((zSec[1] || 0) + (zSec[2] || 0) + (zSec[3] || 0) + (zSec[4] || 0)) / 60);
        if (totalZoneMin > 0) {
          dailyValues[idx] += totalZoneMin;
        }
      } else if (metricType === 'calories' && !w.excludeFromStats) {
        const isRunWorkout = w.type === 'Run' || w.name === 'Run Workout' || w.name === 'Run';
        if (!isRunWorkout) {
          const cal = w.calories || 0;
          if (cal > 0) {
            dailyValues[idx] += cal;
          }
        }
      }
    }
  });
  return dailyValues;
}

export function renderWeeklyTrend(containerId, dailyValues, colorVar, glowVar, metricUnit = '') {
  if (typeof document === 'undefined') return;
  const container = document.getElementById(containerId);
  if (!container) return;
  
  const maxVal = Math.max(...dailyValues);
  const days = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  
  let html = '';
  for (let i = 0; i < 7; i++) {
    const val = dailyValues[i] || 0;
    const pct = maxVal > 0 ? (val / maxVal) * 100 : 0;
    const barHeight = maxVal > 0 ? Math.max((pct / 100) * 50, 4) : 4;
    const formattedVal = Number.isInteger(val) ? val.toLocaleString() : val.toFixed(1);
    const tooltipText = `${dayNames[i]}: ${formattedVal}${metricUnit ? ' ' + metricUnit : ''}`;
    
    html += `
      <div class="trend-bar-wrapper" title="${tooltipText}">
        <div class="trend-bar" style="height: ${barHeight}px; background: ${colorVar}; box-shadow: 0 0 6px ${glowVar};"></div>
        <span class="trend-day-label">${days[i]}</span>
      </div>
    `;
  }
  container.innerHTML = html;
}

export function renderDashboard() {
  if (typeof document === 'undefined') return;

  const goals = store.goals || { suffer: 550, distance: 25.0, calories: 3500 };
  const weeklyAdj = store.weeklyAdjustments || { suffer: 0, distance: 0, calories: 0 };
  const weeklyZoneAdj = store.weeklyZoneAdjustments || { z2: 0, z3: 0, z4: 0, z5: 0 };

  // Update Goal Labels
  if (document.getElementById('txt-suffer-goal')) {
    document.getElementById('txt-suffer-goal').innerText = goals.suffer || 550;
  }
  if (document.getElementById('txt-distance-goal')) {
    document.getElementById('txt-distance-goal').innerText = (goals.distance || 25.0).toFixed(1);
  }
  if (document.getElementById('txt-calories-goal')) {
    document.getElementById('txt-calories-goal').innerText = goals.calories || 3500;
  }

  let sufferTotal = weeklyAdj.suffer || 0;
  let distanceTotal = weeklyAdj.distance || 0;
  let caloriesTotal = weeklyAdj.calories || 0;

  let sufferListHtml = '';
  let distanceListHtml = '';
  let caloriesListHtml = '';
  
  if (store.workouts) {
    const sorted = [...store.workouts].sort((a, b) => getWorkoutTimestamp(b) - getWorkoutTimestamp(a));
    
    sorted.forEach(w => {
      const ts = getWorkoutTimestamp(w);
      if (ts) {
        const localDateStr = new Date(ts).toLocaleDateString('sv');
        if (isDateInCurrentWeek(localDateStr)) {
          const dateLabel = new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          const titleWithEmoji = getTitleWithEmoji(w);

          let dist = getWorkoutTotalDistance(w);
          if (dist > 0) {
            distanceTotal += dist;
            distanceListHtml += `
              <div class="workout-list-item">
                <span class="item-date">${dateLabel}</span>
                <span class="item-title">${escapeHtml(titleWithEmoji)}</span>
                <span class="item-val" style="color: var(--color-distance);">${dist.toFixed(2)} km</span>
              </div>
            `;
          }

          if (!w.excludeFromStats) {
            let wSuffer = w.sufferScore;
            if (wSuffer === null || wSuffer === undefined) {
              const zSec = calculateWorkoutZoneSeconds(w);
              wSuffer = Math.round(
                ((zSec[0] || 0) / 60) * 1.0 +
                ((zSec[1] || 0) / 60) * 2.0 +
                ((zSec[2] || 0) / 60) * 3.0 +
                ((zSec[3] || 0) / 60) * 4.0 +
                ((zSec[4] || 0) / 60) * 5.0
              );
            }
            wSuffer = Math.round(wSuffer * (w.sufferMultiplier || 1));
            if (wSuffer && wSuffer > 0) {
              sufferTotal += wSuffer;
              sufferListHtml += `
                <div class="workout-list-item">
                  <span class="item-date">${dateLabel}</span>
                  <span class="item-title">${escapeHtml(titleWithEmoji)}</span>
                  <span class="item-val" style="color: var(--color-suffer);">${wSuffer} pts</span>
                </div>
              `;
            }

            const isRunWorkout = w.type === 'Run' || w.name === 'Run Workout' || w.type === 'Run stage' || titleWithEmoji.includes('🏃');
            if (!isRunWorkout) {
              const cal = w.calories || 0;
              if (cal > 0) {
                caloriesTotal += cal;
                caloriesListHtml += `
                  <div class="workout-list-item">
                    <span class="item-date">${dateLabel}</span>
                    <span class="item-title">${escapeHtml(titleWithEmoji)}</span>
                    <span class="item-val" style="color: var(--color-calories);">${cal} kcal</span>
                  </div>
                `;
              }
            }
          }
        }
      }
    });
  }

  // Fallbacks if empty
  if (!sufferListHtml) {
    sufferListHtml = `<div style="color: var(--text-muted); text-align: center; font-size: 11px; padding: 10px 0; width: 100%;">No workouts logged this week</div>`;
  }
  if (!distanceListHtml) {
    distanceListHtml = `<div style="color: var(--text-muted); text-align: center; font-size: 11px; padding: 10px 0; width: 100%;">No runs logged this week</div>`;
  }
  if (!caloriesListHtml) {
    caloriesListHtml = `<div style="color: var(--text-muted); text-align: center; font-size: 11px; padding: 10px 0; width: 100%;">No workouts logged this week</div>`;
  }

  const sufferList = document.getElementById('list-suffer-workouts');
  const distanceList = document.getElementById('list-distance-workouts');
  const caloriesList = document.getElementById('list-calories-workouts');
  if (sufferList) sufferList.innerHTML = sufferListHtml;
  if (distanceList) distanceList.innerHTML = distanceListHtml;
  if (caloriesList) caloriesList.innerHTML = caloriesListHtml;

  const suffer = sufferTotal;
  const distance = distanceTotal;
  const calories = caloriesTotal;

  store.weeklyStats = { suffer, distance, calories };

  // Update Ring Math
  updateRing('ring-suffer', suffer, goals.suffer || 550);
  updateRing('ring-distance', distance, goals.distance || 25.0);
  updateRing('ring-calories', calories, goals.calories || 3500);
  
  // Render Weekly Trend Charts
  renderWeeklyTrend('trend-suffer-chart', getWeeklyTrendData('suffer'), 'var(--color-suffer)', 'var(--color-suffer-glow)', 'Suffer');
  renderWeeklyTrend('trend-distance-chart', getWeeklyTrendData('distance'), 'var(--color-distance)', 'var(--color-distance-glow)', 'km');
  renderWeeklyTrend('trend-calories-chart', getWeeklyTrendData('calories'), 'var(--color-calories)', 'var(--color-calories-glow)', 'kcal');
  
  // Update Numeric Labels
  if (document.getElementById('txt-suffer-val')) {
    document.getElementById('txt-suffer-val').innerText = Math.round(suffer).toLocaleString();
  }
  if (document.getElementById('txt-distance-val')) {
    document.getElementById('txt-distance-val').innerText = distance.toFixed(1);
  }
  if (document.getElementById('txt-calories-val')) {
    document.getElementById('txt-calories-val').innerText = Math.round(calories).toLocaleString();
  }
  
  // Update Weekly Zone Targets
  let z2TotalSec = (weeklyZoneAdj.z2 || 0) * 60;
  let z3TotalSec = (weeklyZoneAdj.z3 || 0) * 60;
  let z4TotalSec = (weeklyZoneAdj.z4 || 0) * 60;
  let z5TotalSec = (weeklyZoneAdj.z5 || 0) * 60;

  if (store.workouts) {
    store.workouts.forEach(w => {
      const ts = getWorkoutTimestamp(w);
      if (ts) {
        const localDateStr = new Date(ts).toLocaleDateString('sv');
        if (isDateInCurrentWeek(localDateStr)) {
          if (w.excludeFromStats) return;
          const zSec = calculateWorkoutZoneSeconds(w);
          z2TotalSec += (zSec[1] || 0);
          z3TotalSec += (zSec[2] || 0);
          z4TotalSec += (zSec[3] || 0);
          z5TotalSec += (zSec[4] || 0);
        }
      }
    });
  }

  const zoneGoals = store.weeklyZoneGoals || { z2: 120, z3: 60, z4: 60, z5: 30 };

  const z2 = Math.round(z2TotalSec / 60);
  const z3 = Math.round(z3TotalSec / 60);
  const z4 = Math.round(z4TotalSec / 60);
  const z5 = Math.round(z5TotalSec / 60);

  store.weeklyZones = { z2, z3, z4, z5 };
  
  const z2Goal = zoneGoals.z2 || 120;
  const z3Goal = zoneGoals.z3 || 60;
  const z4Goal = zoneGoals.z4 || 60;
  const z5Goal = zoneGoals.z5 || 30;
  
  if (document.getElementById('txt-z2-val')) document.getElementById('txt-z2-val').innerText = z2;
  if (document.getElementById('txt-z3-val')) document.getElementById('txt-z3-val').innerText = z3;
  if (document.getElementById('txt-z4-val')) document.getElementById('txt-z4-val').innerText = z4;
  if (document.getElementById('txt-z5-val')) document.getElementById('txt-z5-val').innerText = z5;
  
  if (document.getElementById('txt-z2-goal')) document.getElementById('txt-z2-goal').innerText = z2Goal;
  if (document.getElementById('txt-z3-goal')) document.getElementById('txt-z3-goal').innerText = z3Goal;
  if (document.getElementById('txt-z4-goal')) document.getElementById('txt-z4-goal').innerText = z4Goal;
  if (document.getElementById('txt-z5-goal')) document.getElementById('txt-z5-goal').innerText = z5Goal;
  
  if (document.getElementById('bar-z2')) document.getElementById('bar-z2').style.width = `${Math.min((z2 / z2Goal) * 100, 100)}%`;
  if (document.getElementById('bar-z3')) document.getElementById('bar-z3').style.width = `${Math.min((z3 / z3Goal) * 100, 100)}%`;
  if (document.getElementById('bar-z4')) document.getElementById('bar-z4').style.width = `${Math.min((z4 / z4Goal) * 100, 100)}%`;
  if (document.getElementById('bar-z5')) document.getElementById('bar-z5').style.width = `${Math.min((z5 / z5Goal) * 100, 100)}%`;

  const zonesTotal = z2 + z3 + z4 + z5;
  const zonesGoal = z2Goal + z3Goal + z4Goal + z5Goal;
  updateRing('ring-zones', zonesTotal, zonesGoal);
  
  if (document.getElementById('txt-zones-val')) {
    document.getElementById('txt-zones-val').innerText = Math.round(zonesTotal).toLocaleString();
  }
  if (document.getElementById('txt-zones-goal')) {
    document.getElementById('txt-zones-goal').innerText = Math.round(zonesGoal).toLocaleString();
  }

  renderWeeklyTrend('trend-zones-chart', getWeeklyTrendData('zones'), 'var(--color-minutes)', 'var(--color-minutes-glow)', 'min');

  let zoneListHtml = '';
  if (store.workouts) {
    const sorted = [...store.workouts].sort((a, b) => getWorkoutTimestamp(b) - getWorkoutTimestamp(a));
    sorted.forEach(w => {
      const ts = getWorkoutTimestamp(w);
      if (ts) {
        const localDateStr = new Date(ts).toLocaleDateString('sv');
        if (isDateInCurrentWeek(localDateStr)) {
          const zSec = calculateWorkoutZoneSeconds(w);
          const z2m = Math.round((zSec[1] || 0) / 60);
          const z3m = Math.round((zSec[2] || 0) / 60);
          const z4m = Math.round((zSec[3] || 0) / 60);
          const z5m = Math.round((zSec[4] || 0) / 60);
          const totalZoneMin = z2m + z3m + z4m + z5m;
          if (totalZoneMin > 0) {
            const dateLabel = new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            const titleWithEmoji = getTitleWithEmoji(w);
            zoneListHtml += `
              <div class="workout-list-item">
                <span class="item-date">${dateLabel}</span>
                <span class="item-title">${escapeHtml(titleWithEmoji)}</span>
                <span class="item-val" style="color: var(--color-minutes);">${totalZoneMin} min</span>
              </div>
            `;
          }
        }
      }
    });
  }
  if (!zoneListHtml) {
    zoneListHtml = `<div style="color: var(--text-muted); text-align: center; font-size: 11px; padding: 10px 0; width: 100%;">No workouts logged this week</div>`;
  }
  const zoneList = document.getElementById('list-zone-workouts');
  if (zoneList) zoneList.innerHTML = zoneListHtml;

  const welcomeText = document.getElementById('welcome-message');
  if (welcomeText) {
    const pct = (suffer / (goals.suffer || 550)) * 100;
    if (pct >= 100) {
      welcomeText.innerText = "Weekly goal achieved! Excellent work!";
    } else if (pct >= 50) {
      welcomeText.innerText = "More than halfway! Keep moving!";
    } else {
      welcomeText.innerText = "";
    }
  }

  renderStationTiles();
  if (typeof window !== 'undefined' && typeof window.renderSavedPlannedWorkouts === 'function') {
    window.renderSavedPlannedWorkouts();
  }
  if (document.getElementById('view-performance')?.classList.contains('active')) {
    if (typeof window !== 'undefined' && typeof window.renderPerformanceView === 'function') {
      window.renderPerformanceView();
    }
  }
}

export function renderStationTiles() {
  if (typeof document === 'undefined') return;
  const grid = document.getElementById('station-tiles-grid');
  if (!grid) return;

  function stageSec(st) {
    return (st.durationMin || 0) * 60 + (st.durationSec || 0);
  }
  function fmtTime(sec) {
    if (!sec && sec !== 0) return '—';
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  }

  const stationData = {};
  STATION_META.forEach(m => {
    stationData[m.key] = { count: 0, totalSec: 0, best: null, totalHr: 0, hrCount: 0, totalMetric: 0, splits: [] };
  });

  if (store.workouts) {
    store.workouts.forEach(w => {
      const isHyrox = w.type === 'Hyrox' || w.type === 'Hyrox Training' || w.type === 'Hyrox Workout' || (w.name && w.name.toLowerCase().includes('hyrox')) || !w.type;
      if (!isHyrox || !Array.isArray(w.stages)) return;
      const ts = getWorkoutTimestamp(w);
      if (!ts) return;
      if (!isDateInCurrentWeek(new Date(ts).toLocaleDateString('sv'))) return;

      const dateLabel = new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const workoutName = w.name || 'Hyrox';

      w.stages.forEach(st => {
        if (!st.stationName || st.type === 'Run' || st.type === 'Rest' || st.type === 'RoxZone' || st.type === 'Cardio') return;
        let key = st.stationName;
        let parent = key.includes(':') ? key.split(':')[0].trim() : key;
        if (parent === 'Burpee Broad Jump') parent = 'Burpee Broad Jumps';
        if (parent === "Farmer's Carry" || parent === 'Farmer Carry') parent = 'Farmers Carry';
        if (parent === 'Ski Erg') parent = 'SkiErg';

        if (stationData[parent]) {
          key = parent;
        } else if (!stationData[key]) {
          return;
        }

        const sec = stageSec(st);
        if (sec <= 0) return;

        const d = stationData[key];
        d.count++;
        d.totalSec += sec;
        if (d.best === null || sec < d.best) d.best = sec;

        const hr = st.avgHr || 0;
        if (hr > 0) { d.totalHr += hr; d.hrCount++; }

        const metaObj = STATION_META.find(m => m.key === key);
        const metric = (metaObj && !metaObj.isDistance) ? (st.reps || st.distance || 0) : (st.distance || st.reps || 0);
        d.totalMetric += metric;

        const altDetail = st.stationName.includes(':') ? st.stationName.split(':')[1].trim() : null;
        const displayName = altDetail ? `${workoutName} (${altDetail})` : workoutName;

        d.splits.push({
          date: dateLabel,
          name: displayName,
          sec,
          hr,
          metric,
        });
      });
    });
  }

  grid.innerHTML = STATION_META.map(meta => {
    const d = stationData[meta.key];
    const avgSec     = d.count > 0 ? Math.round(d.totalSec / d.count) : null;
    const avgHr      = d.hrCount > 0 ? Math.round(d.totalHr / d.hrCount) : null;
    const metricUnit = meta.isDistance ? 'm' : 'reps';
    const totalMetricStr = d.count > 0 ? `${d.totalMetric} ${metricUnit}` : '—';

    const stationGoals = (store.goals && store.goals.stations) || {};
    const goal = stationGoals[meta.key] || null;
    const progressPct = (goal && d.totalMetric > 0) ? Math.min((d.totalMetric / goal) * 100, 100) : 0;
    const goalLabel = goal ? `${d.totalMetric} / ${goal} ${metricUnit}` : totalMetricStr;
    const goalReached = goal && d.totalMetric >= goal;

    const progressBarHtml = goal ? `
      <div style="margin-top:4px;">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;">
          <span style="font-size:11px;text-transform:uppercase;letter-spacing:.5px;color:var(--text-muted);font-weight:600;">Weekly Goal</span>
          <span style="font-size:13px;font-weight:700;color:${goalReached ? '#34d399' : 'var(--st-accent,#6366f1)'};">${goalLabel}${goalReached ? ' ✓' : ''}</span>
        </div>
        <div style="height:8px;border-radius:4px;background:rgba(255,255,255,0.06);overflow:hidden;">
          <div style="width:${progressPct.toFixed(1)}%;height:100%;background:${goalReached ? '#34d399' : 'var(--st-accent,#6366f1)'};border-radius:4px;transition:width .4s ease;box-shadow:0 0 6px color-mix(in srgb,var(--st-accent,#6366f1) 60%,transparent);"></div>
        </div>
      </div>` : '';

    const statsHtml = `
      <div class="station-tile-stats">
        <div class="station-tile-stat">
          <span class="station-tile-stat-val">${d.count || '—'}</span>
          <span class="station-tile-stat-lbl">Sets</span>
        </div>
        <div class="station-tile-stat">
          <span class="station-tile-stat-val">${d.best !== null ? fmtTime(d.best) : '—'}</span>
          <span class="station-tile-stat-lbl">Best Split</span>
        </div>
        <div class="station-tile-stat">
          <span class="station-tile-stat-val">${avgSec !== null ? fmtTime(avgSec) : '—'}</span>
          <span class="station-tile-stat-lbl">Avg Split</span>
        </div>
        <div class="station-tile-stat">
          <span class="station-tile-stat-val">${avgHr !== null ? `${avgHr}` : '—'}</span>
          <span class="station-tile-stat-lbl">Avg HR</span>
        </div>
        <div class="station-tile-stat" style="grid-column: span 2;">
          <span class="station-tile-stat-val" style="font-size:12px;">${totalMetricStr}</span>
          <span class="station-tile-stat-lbl">Total ${metricUnit === 'm' ? 'Distance' : 'Reps'}</span>
        </div>
      </div>`;

    const splitsHtml = d.splits.length > 0
      ? `<div class="station-tile-splits">${
          d.splits.map(sp => `
            <div class="station-tile-split-row">
              <span>${sp.date} · ${escapeHtml(sp.name)}</span>
              <span style="display:flex;gap:8px;align-items:center;">
                ${sp.metric ? `<span style="color:var(--text-muted);font-size:9px;">${metricUnit === 'reps' ? `${sp.metric} reps` : `${sp.metric}m`}</span>` : ''}
                ${sp.hr ? `<span style="color:#f87171;font-size:9px;">♥ ${sp.hr}</span>` : ''}
                <span>${fmtTime(sp.sec)}</span>
              </span>
            </div>`).join('')
        }</div>`
      : `<div class="station-tile-empty">No sets logged this week</div>`;

    return `
      <div class="station-tile" data-station="${meta.key}" style="--st-accent:${meta.accent}; cursor: pointer;">
        <div class="station-tile-header">
          ${getStationBadgeHtml(meta.key)}
          <span class="station-tile-name">${meta.label}</span>
        </div>
        ${progressBarHtml}
        ${statsHtml}
        ${splitsHtml}
      </div>`;
  }).join('');

  const tiles = grid.querySelectorAll('.station-tile');
  tiles.forEach(tile => {
    tile.addEventListener('click', () => {
      const stationKey = tile.getAttribute('data-station');
      openStationDetailModal(stationKey);
    });
  });
}

export function openStationDetailModal(stationKey) {
  if (typeof document === 'undefined') return;
  const meta = STATION_META.find(m => m.key === stationKey);
  if (!meta) return;

  function stageSec(st) {
    return (st.durationMin || 0) * 60 + (st.durationSec || 0);
  }
  function fmtTime(sec) {
    if (!sec && sec !== 0) return '—';
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  }

  const d = { count: 0, totalSec: 0, best: null, totalHr: 0, hrCount: 0, totalMetric: 0, splits: [] };

  if (store.workouts) {
    store.workouts.forEach(w => {
      const isHyrox = w.type === 'Hyrox' || w.type === 'Hyrox Training' || w.type === 'Hyrox Workout' || (w.name && w.name.toLowerCase().includes('hyrox')) || !w.type;
      if (!isHyrox || !Array.isArray(w.stages)) return;
      const ts = getWorkoutTimestamp(w);
      if (!ts) return;
      if (!isDateInCurrentWeek(new Date(ts).toLocaleDateString('sv'))) return;

      const dateLabel = new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const workoutName = w.name || 'Hyrox';

      w.stages.forEach(st => {
        if (st.stationName !== stationKey || st.type === 'Run' || st.type === 'Rest' || st.type === 'RoxZone' || st.type === 'Cardio') return;

        const sec = stageSec(st);
        if (sec <= 0) return;

        d.count++;
        d.totalSec += sec;
        if (d.best === null || sec < d.best) d.best = sec;

        const hr = st.avgHr || 0;
        if (hr > 0) { d.totalHr += hr; d.hrCount++; }

        const metric = !meta.isDistance ? (st.reps || st.distance || 0) : (st.distance || st.reps || 0);
        d.totalMetric += metric;

        d.splits.push({
          date: dateLabel,
          name: workoutName,
          sec,
          hr,
          metric,
        });
      });
    });
  }

  const avgSec     = d.count > 0 ? Math.round(d.totalSec / d.count) : null;
  const avgHr      = d.hrCount > 0 ? Math.round(d.totalHr / d.hrCount) : null;
  const metricUnit = meta.isDistance ? 'm' : 'reps';
  const totalMetricStr = d.count > 0 ? `${d.totalMetric} ${metricUnit}` : '—';

  const stationGoals = (store.goals && store.goals.stations) || {};
  const goal = stationGoals[meta.key] || null;
  const progressPct = (goal && d.totalMetric > 0) ? Math.min((d.totalMetric / goal) * 100, 100) : 0;
  const goalLabel = goal ? `${d.totalMetric} / ${goal} ${metricUnit}` : totalMetricStr;
  const goalReached = goal && d.totalMetric >= goal;

  const progressBarHtml = goal ? `
    <div style="margin-bottom:15px; width: 100%;">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;">
        <span style="font-size:10px;text-transform:uppercase;letter-spacing:.5px;color:var(--text-muted);font-weight:600;">Weekly Goal Progress</span>
        <span style="font-size:11px;font-weight:700;color:${goalReached ? '#34d399' : meta.accent};">${goalLabel}${goalReached ? ' ✓' : ''}</span>
      </div>
      <div style="height:8px;border-radius:4px;background:rgba(255,255,255,0.06);overflow:hidden;border: 1px solid rgba(255,255,255,0.02);">
        <div style="width:${progressPct.toFixed(1)}%;height:100%;background:${goalReached ? '#34d399' : meta.accent};border-radius:4px;transition:width .4s ease;box-shadow:0 0 8px ${meta.accent}55;"></div>
      </div>
    </div>` : '';

  const statsHtml = `
    <div class="station-tile-stats" style="grid-template-columns: repeat(2, 1fr); gap: 12px; margin-bottom: 5px;">
      <div class="station-tile-stat" style="padding: 12px 14px;">
        <span class="station-tile-stat-val" style="font-size: 18px; font-weight:700; color:var(--text-primary);">${d.count || '—'}</span>
        <span class="station-tile-stat-lbl" style="font-size: 10px; color:var(--text-muted); margin-top:2px;">Sets Completed</span>
      </div>
      <div class="station-tile-stat" style="padding: 12px 14px;">
        <span class="station-tile-stat-val" style="font-size: 18px; font-weight:700; color:var(--text-primary);">${d.best !== null ? fmtTime(d.best) : '—'}</span>
        <span class="station-tile-stat-lbl" style="font-size: 10px; color:var(--text-muted); margin-top:2px;">Best Split Time</span>
      </div>
      <div class="station-tile-stat" style="padding: 12px 14px;">
        <span class="station-tile-stat-val" style="font-size: 18px; font-weight:700; color:var(--text-primary);">${avgSec !== null ? fmtTime(avgSec) : '—'}</span>
        <span class="station-tile-stat-lbl" style="font-size: 10px; color:var(--text-muted); margin-top:2px;">Average Split Time</span>
      </div>
      <div class="station-tile-stat" style="padding: 12px 14px;">
        <span class="station-tile-stat-val" style="font-size: 18px; font-weight:700; color:var(--text-primary);">${avgHr !== null ? `${avgHr} bpm` : '—'}</span>
        <span class="station-tile-stat-lbl" style="font-size: 10px; color:var(--text-muted); margin-top:2px;">Average Heart Rate</span>
      </div>
      <div class="station-tile-stat" style="grid-column: span 2; padding: 12px 14px; display:flex; justify-content:space-between; align-items:center;">
        <span class="station-tile-stat-lbl" style="font-size: 10px; color:var(--text-muted);">Accumulated Volume:</span>
        <span class="station-tile-stat-val" style="font-size: 15px; font-weight:700; color:${meta.accent};">${totalMetricStr}</span>
      </div>
    </div>`;

  const splitsHtml = d.splits.length > 0
    ? `<div style="display:flex; flex-direction:column; gap:8px;">${
        d.splits.map(sp => `
          <div class="station-tile-split-row" style="background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.05); border-radius: 8px; padding: 10px 14px; display:flex; justify-content:space-between; align-items:center; font-size:12px;">
            <span style="font-weight:500; color:var(--text-primary);">${sp.date} · ${escapeHtml(sp.name)}</span>
            <span style="display:flex; gap:12px; align-items:center; font-weight:600;">
              ${sp.metric ? `<span style="color:var(--text-secondary); font-size:11px; background:rgba(255,255,255,0.04); padding:2px 6px; border-radius:4px;">${metricUnit === 'reps' ? `${sp.metric} reps` : `${sp.metric}m`}</span>` : ''}
              ${sp.hr ? `<span style="color:#f43f5e; font-size:11px; background:rgba(244,63,94,0.05); padding:2px 6px; border-radius:4px; display:inline-flex; align-items:center; gap:2px;">♥ ${sp.hr}</span>` : ''}
              <span style="color:var(--text-primary); font-family:monospace; font-size:12px;">${fmtTime(sp.sec)}</span>
            </span>
          </div>`).join('')
      }</div>`
    : `<div style="color: var(--text-muted); text-align:center; padding: 20px 0; font-size:12px; background:rgba(255,255,255,0.01); border-radius:8px; border:1px dashed rgba(255,255,255,0.05);">No sets logged this week</div>`;

  const modalIconEl = document.getElementById('station-modal-icon');
  if (modalIconEl) modalIconEl.innerHTML = getStationBadgeHtml(meta.key, 'station-badge-sm');
  if (document.getElementById('station-modal-name')) document.getElementById('station-modal-name').innerText = meta.label;
  if (document.getElementById('station-modal-progress-container')) document.getElementById('station-modal-progress-container').innerHTML = progressBarHtml;
  if (document.getElementById('station-modal-stats-container')) document.getElementById('station-modal-stats-container').innerHTML = statsHtml;
  if (document.getElementById('station-modal-splits-container')) document.getElementById('station-modal-splits-container').innerHTML = splitsHtml;

  setupStationDetailModal();
  const dialog = document.getElementById('station-detail-dialog');
  if (dialog && typeof dialog.showModal === 'function') {
    dialog.showModal();
  }
}

let stationModalSetupDone = false;
export function setupStationDetailModal() {
  if (typeof document === 'undefined' || stationModalSetupDone) return;
  stationModalSetupDone = true;

  const stationDialog = document.getElementById('station-detail-dialog');
  const closeStationBtn = document.getElementById('close-station-modal-btn');
  const closeStationFooterBtn = document.getElementById('btn-close-station-modal');

  if (stationDialog) {
    if (closeStationBtn) {
      closeStationBtn.addEventListener('click', () => {
        if (typeof stationDialog.close === 'function') stationDialog.close();
      });
    }
    if (closeStationFooterBtn) {
      closeStationFooterBtn.addEventListener('click', () => {
        if (typeof stationDialog.close === 'function') stationDialog.close();
      });
    }
    stationDialog.addEventListener('click', (e) => {
      if (e.target === stationDialog && typeof stationDialog.close === 'function') {
        stationDialog.close();
      }
    });
  }
}

export function renderAchievements() {
  if (typeof document === 'undefined') return;
  const container = document.getElementById('badges-container');
  if (!container) return;
  container.innerHTML = '';
  
  const categories = {};
  BADGES.forEach(badge => {
    const cat = badge.category || 'Other';
    if (!categories[cat]) categories[cat] = [];
    categories[cat].push(badge);
  });

  Object.keys(categories).forEach(catName => {
    const header = document.createElement('h3');
    header.className = 'achievements-category-title';
    header.innerText = catName;
    header.style.cssText = `
      grid-column: 1 / -1;
      font-size: 14px;
      font-weight: 700;
      color: var(--color-primary);
      margin: 25px 0 10px;
      letter-spacing: 1px;
      text-transform: uppercase;
      opacity: 0.85;
      border-bottom: 1px solid var(--border-light);
      padding-bottom: 6px;
    `;
    container.appendChild(header);

    categories[catName].forEach(badge => {
      const isUnlocked = badge.check(store);
      const card = document.createElement('div');
      card.className = `badge-card glass ${isUnlocked ? 'unlocked' : ''}`;
      card.id = badge.id;
      
      const descText = typeof badge.desc === 'function' ? badge.desc(store) : badge.desc;
      
      card.innerHTML = `
        <div class="badge-icon-wrap">${badge.icon}</div>
        <div class="badge-info">
          <span class="badge-title">${badge.title}</span>
          <span class="badge-desc">${descText}</span>
          <span class="badge-status"></span>
        </div>
      `;
      
      container.appendChild(card);
    });
  });
}

export function setupQuickAdjustmentButtons() {
  if (typeof document === 'undefined') return;

  const quickButtons = document.querySelectorAll('.btn-quick');
  quickButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      const type = btn.getAttribute('data-type');
      const value = parseFloat(btn.getAttribute('data-val'));
      
      if (!store.weeklyAdjustments) store.weeklyAdjustments = { suffer: 0, distance: 0, calories: 0 };
      store.weeklyAdjustments[type] = (store.weeklyAdjustments[type] || 0) + value;
      saveStateToStorage();
      
      renderDashboard();
      renderAchievements();
    });
  });
  
  const resetDayBtn = document.getElementById('reset-day-btn');
  if (resetDayBtn) {
    resetDayBtn.addEventListener('click', () => {
      if (confirm('Are you sure you want to reset this week\'s fitness manual dashboard logs? This cannot be undone.')) {
        store.weeklyAdjustments = { suffer: 0, distance: 0, calories: 0 };
        saveStateToStorage();
        renderDashboard();
        renderAchievements();
      }
    });
  }

  const exportBtn = document.getElementById('btn-export-data');
  if (exportBtn) {
    exportBtn.addEventListener('click', () => {
      try {
        const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(store, null, 2));
        const downloadAnchor = document.createElement('a');
        downloadAnchor.setAttribute("href", dataStr);
        const timestamp = new Date().toISOString().slice(0, 10);
        downloadAnchor.setAttribute("download", `roxkit-backup-${timestamp}.json`);
        document.body.appendChild(downloadAnchor);
        downloadAnchor.click();
        downloadAnchor.remove();
        if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
          window.showToast('📥 Backup exported successfully!');
        }
      } catch (e) {
        console.error(e);
        if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
          window.showToast('❌ Export failed.', 'error');
        }
      }
    });
  }

  const importTrigger = document.getElementById('btn-import-data-trigger');
  const importFileInput = document.getElementById('input-import-file');

  if (importTrigger && importFileInput) {
    importTrigger.addEventListener('click', () => {
      importFileInput.click();
    });

    importFileInput.addEventListener('change', (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = function(evt) {
        try {
          const importedState = JSON.parse(evt.target.result);
          if (importedState && typeof importedState === 'object' && (importedState.goals || importedState.workouts)) {
            Object.assign(store, importedState);
            saveStateToStorage();
            if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
              window.showToast('✅ Data imported successfully! Reloading...');
            }
            setTimeout(() => {
              window.location.reload();
            }, 1200);
          } else {
            if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
              window.showToast('❌ Invalid backup file format.', 'error');
            }
          }
        } catch (err) {
          console.error(err);
          if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
            window.showToast('❌ Error parsing JSON file.', 'error');
          }
        }
        importFileInput.value = '';
      };
      reader.readAsText(file);
    });
  }

  const refreshBtn = document.getElementById('btn-dashboard-refresh');
  if (refreshBtn) {
    refreshBtn.addEventListener('click', async () => {
      refreshBtn.disabled = true;
      refreshBtn.innerText = '⏳ Syncing...';
      try {
        if (typeof window !== 'undefined' && typeof window.showToast === 'function') {
          window.showToast('Dashboard metrics refreshed successfully!');
        }
      } catch (e) {
        console.error(e);
      } finally {
        renderDashboard();
        renderAchievements();
        refreshBtn.disabled = false;
        refreshBtn.innerText = '🔄 Refresh';
      }
    });
  }
}

let isDashboardInitialized = false;

export function initDashboardView() {
  if (isDashboardInitialized) return;
  isDashboardInitialized = true;
  subscribeKey('goals', renderDashboard);
  subscribeKey('weeklyAdjustments', renderDashboard);
  subscribeKey('weeklyZoneAdjustments', renderDashboard);
  subscribeKey('weeklyZoneGoals', renderDashboard);
  setupQuickAdjustmentButtons();
  setupStationDetailModal();
}
